import { LIST_MAX_SIZE, PageParams, SortParam } from "../types/pagination";

/**
 * Clamp a requested page size to the range the backend accepts (1..200).
 * @param {number | undefined} size - requested page size
 * @param {number} fallback - size used when none is given
 * @returns {number} a valid page size
 */
export const clampPageSize = (size: number | undefined, fallback: number): number => {
  const value = size ?? fallback;
  return Math.min(Math.max(Math.trunc(value), 1), LIST_MAX_SIZE);
};

/**
 * Append paging and sorting parameters shared by every list endpoint.
 * Sort keys are repeated (`sort=name:asc&sort=created_at:desc`), which is what FastAPI expects for list queries.
 * @param {URLSearchParams} params - target params, mutated in place
 * @param {PageParams} options - paging and sort options
 * @param {number} defaultSize - page size used when `options.size` is not set
 */
export const appendPageParams = (params: URLSearchParams, options: PageParams, defaultSize: number): void => {
  params.set('page', String(Math.max(options.page ?? 1, 1)));
  params.set('size', String(clampPageSize(options.size, defaultSize)));
  (options.sort ?? []).forEach((sort: SortParam) => params.append('sort', `${sort.field}:${sort.direction}`));
};

/**
 * Append a repeatable UUID/int list filter under the backend alias (e.g. `owner_id`).
 * @param {URLSearchParams} params - target params, mutated in place
 * @param {string} key - backend query key
 * @param {Array<string | number> | undefined} values - filter values
 */
export const appendListFilter = (params: URLSearchParams, key: string, values: Array<string | number> | undefined): void => {
  (values ?? []).forEach((value) => params.append(key, String(value)));
};

/**
 * Append a string filter when it has a non-empty trimmed value.
 * @param {URLSearchParams} params - target params, mutated in place
 * @param {string} key - backend query key
 * @param {string | undefined} value - filter value
 */
export const appendTextFilter = (params: URLSearchParams, key: string, value: string | undefined): void => {
  const trimmed = value?.trim();
  if (trimmed) params.set(key, trimmed);
};

/**
 * Append a boolean flag only when it is true, so default-false filters are omitted.
 * @param {URLSearchParams} params - target params, mutated in place
 * @param {string} key - backend query key
 * @param {boolean | undefined} value - flag value
 */
export const appendFlag = (params: URLSearchParams, key: string, value: boolean | undefined): void => {
  if (value) params.set(key, 'true');
};
